import { useMemo, useState } from 'react'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { MapPin } from 'lucide-react'

import {
  useAcquisitionAiAssessments,
  useAcquisitionTargets,
  useFacility,
  useForecast,
} from './api'
import {
  CashflowV2Error,
  CashflowV2Loading,
  CashflowV2Unconfigured,
  looksUnconfigured,
} from './components/PageStates'
import { colors, fmt } from './utils'
import type {
  AcquisitionAiAssessment,
  AcquisitionTarget,
  FacilityConfig,
  WeeklyForecastRow,
} from './types'

function cashFractionFromDealStructure(dealStructure: string): number {
  const normalized = dealStructure.toLowerCase()
  if (normalized.includes('equity')) return 0.3
  if (normalized.includes('seller')) return 0.5
  return 0.6
}

function computeImpact(
  target: AcquisitionTarget,
  closeWeek: number,
  forecast: WeeklyForecastRow[],
  covenantMin: number,
) {
  const askingPrice = target.asking_price
  const cashFraction = cashFractionFromDealStructure(target.deal_structure ?? '')
  const cashAtClose = askingPrice * cashFraction
  const integrationCost = askingPrice * 0.02
  const integrationWeeks = Math.max(1, target.integration_weeks)
  const weeklyRevenue = target.annual_revenue / 52
  const weeklyPayroll = (target.monthly_payroll * 12) / 52
  const netPerWeek = weeklyRevenue * 0.55 - weeklyPayroll

  const data = forecast.slice(0, 13).map((week, i) => {
    const weekNum = i + 1
    const baseline = week.ending_cash
    let adjusted = baseline

    if (weekNum > closeWeek) {
      const postWeeks = weekNum - closeWeek
      let cumulative = 0
      for (let k = 1; k <= postWeeks; k++) {
        const ramp = Math.min(1, k / integrationWeeks)
        cumulative += netPerWeek * ramp
      }
      const integrationHit = postWeeks <= 4 ? integrationCost : 0
      adjusted = baseline - cashAtClose + cumulative - integrationHit
    }

    const headroom = adjusted - covenantMin
    return {
      week: `W${weekNum}`,
      weekNum,
      weekEnding: week.week_ending,
      baseline,
      adjusted,
      delta: adjusted - baseline,
      headroom,
    }
  })

  const postClose = data.filter(point => point.weekNum > closeWeek)
  const minHeadroomPost =
    postClose.length > 0
      ? Math.min(...postClose.map(point => point.headroom))
      : Number.POSITIVE_INFINITY

  let weeksToCashPositive: number | null = null
  for (const point of data) {
    if (point.weekNum > closeWeek && point.adjusted > point.baseline) {
      weeksToCashPositive = point.weekNum - closeWeek
      break
    }
  }

  const rampAt13 = Math.min(1, (13 - closeWeek) / integrationWeeks)
  const monthlyContributionAtRamp =
    (weeklyRevenue * 0.55 - weeklyPayroll) * rampAt13 * (52 / 12)

  return {
    data,
    cashAtClose,
    integrationCost,
    minHeadroomPost: Number.isFinite(minHeadroomPost) ? minHeadroomPost : 0,
    weeksToCashPositive,
    monthlyContributionAtRamp,
  }
}

function KpiCard({
  label,
  value,
  subtext,
  color,
}: {
  label: string
  value: string
  subtext?: string
  color?: string
}) {
  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-4 flex flex-col gap-1 min-w-0 dark:bg-dark-surface dark:border-dark-border">
      <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {label}
      </span>
      <span
        className="text-lg font-semibold truncate text-gray-900 dark:text-gray-100"
        style={color ? { color } : undefined}
      >
        {value}
      </span>
      {subtext && (
        <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate">
          {subtext}
        </span>
      )}
    </div>
  )
}

function RiskBadge({ level }: { level: string }) {
  const normalized = level?.toLowerCase?.() ?? ''
  const className =
    normalized.includes('low') || normalized === 'green'
      ? 'bg-emerald-50 text-emerald-700'
      : normalized.includes('medium') || normalized === 'yellow'
        ? 'bg-amber-50 text-amber-700'
        : 'bg-red-50 text-red-700'
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${className}`}>
      {level}
    </span>
  )
}

function scoreTotal(assessment?: AcquisitionAiAssessment) {
  const score = assessment?.financial_score as Record<string, unknown> | undefined
  const total = score?.total_score
  return typeof total === 'number' ? total : null
}

export default function AcquisitionSimPage() {
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null)
  const [closeWeek, setCloseWeek] = useState(4)
  const [mode, setMode] = useState<'acquire' | 'divest'>('acquire')

  const targetsQuery = useAcquisitionTargets()
  const assessmentsQuery = useAcquisitionAiAssessments()
  const forecastQuery = useForecast()
  const facilityQuery = useFacility()

  const targets = useMemo(() => targetsQuery.data ?? [], [targetsQuery.data])
  const assessments = useMemo(
    () => assessmentsQuery.data ?? [],
    [assessmentsQuery.data],
  )
  const forecast = useMemo(() => forecastQuery.data ?? [], [forecastQuery.data])
  const facility: FacilityConfig | undefined = facilityQuery.data

  const selected = selectedIdx !== null ? targets[selectedIdx] ?? null : null

  const assessment = useMemo(() => {
    if (!selected) return undefined
    return assessments.find(item => item.company_name === selected.company_name)
  }, [assessments, selected])

  const covenantMin = facility?.minimum_cash_covenant ?? 0

  const impact = useMemo(() => {
    if (!selected || forecast.length === 0) return null
    return computeImpact(selected, closeWeek, forecast, covenantMin)
  }, [closeWeek, covenantMin, forecast, selected])

  const comparisonData = useMemo(() => {
    if (!targets.length || forecast.length === 0) return []
    return targets.map(target => ({
      target,
      ...computeImpact(target, 4, forecast, covenantMin),
    }))
  }, [covenantMin, forecast, targets])

  const loading =
    targetsQuery.isLoading ||
    assessmentsQuery.isLoading ||
    forecastQuery.isLoading ||
    facilityQuery.isLoading

  const hasError =
    targetsQuery.error ||
    assessmentsQuery.error ||
    forecastQuery.error ||
    facilityQuery.error

  if (loading) {
    return <CashflowV2Loading label="Loading acquisition simulator…" />
  }

  if (hasError) {
    if (looksUnconfigured(hasError)) {
      return <CashflowV2Unconfigured />
    }
    return (
      <CashflowV2Error
        title="Failed to load acquisition simulator data"
        description={(hasError as Error).message}
        onRetry={() => {
          targetsQuery.refetch()
          assessmentsQuery.refetch()
          forecastQuery.refetch()
          facilityQuery.refetch()
        }}
      />
    )
  }

  const beforeDrawn = facility?.current_outstanding ?? 0
  const totalCommit = facility?.total_commitment ?? 1
  const afterDrawn = beforeDrawn + Math.max(0, (impact?.cashAtClose ?? 0) - 5_000_000)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          Acquisition Simulator
        </h1>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Model acquisition impact on liquidity using the 13-week forecast and
          facility covenants.
        </p>
      </div>

      <div className="flex gap-2">
        {(['acquire', 'divest'] as const).map(view => (
          <button
            key={view}
            type="button"
            onClick={() => setMode(view)}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
              mode === view
                ? 'bg-teal-600 text-white border-teal-600'
                : 'bg-white dark:bg-dark-surface text-gray-600 dark:text-gray-300 border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-surface-2'
            }`}
          >
            {view === 'acquire' ? 'Acquire' : 'Divest'}
          </button>
        ))}
      </div>

      {mode === 'divest' ? (
        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-100 dark:border-dark-border shadow-sm p-10 text-center text-gray-500 dark:text-gray-400 text-sm">
          Divestiture simulation coming soon.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {targets.map((target, index) => (
              <button
                key={target.company_name}
                type="button"
                onClick={() => setSelectedIdx(index)}
                className={`rounded-xl border text-left p-4 transition-all shadow-sm ${
                  selectedIdx === index
                    ? 'border-teal-500 ring-2 ring-teal-500/20 bg-teal-50/30'
                    : 'border-gray-100 dark:border-dark-border bg-white dark:bg-dark-surface hover:border-gray-200 dark:hover:border-gray-500'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                    {target.company_name}
                  </div>
                  <RiskBadge level={target.risk_level} />
                </div>
                <div className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400 mb-2">
                  <MapPin className="shrink-0" size={12} />
                  <span>{target.location}</span>
                </div>
                <p className="text-[10px] text-gray-600 dark:text-gray-300 line-clamp-2 mb-2">
                  {target.service_lines.join(', ')}
                </p>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px]">
                  <span className="text-gray-500 dark:text-gray-400">Revenue</span>
                  <span className="font-mono text-gray-900 dark:text-gray-100 text-right">
                    {fmt.compact(target.annual_revenue)}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">EBITDA</span>
                  <span className="font-mono text-gray-900 dark:text-gray-100 text-right">
                    {fmt.compact(target.ebitda)}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">Ask</span>
                  <span className="font-mono text-gray-900 dark:text-gray-100 text-right">
                    {fmt.compact(target.asking_price)}
                  </span>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-100 dark:border-dark-border text-[10px] text-gray-600 dark:text-gray-300">
                  <span className="text-gray-500 dark:text-gray-400">Strategic fit: </span>
                  {target.strategic_fit}
                </div>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-4 bg-white dark:bg-dark-surface rounded-xl border border-gray-100 dark:border-dark-border p-4 shadow-sm">
            <span className="text-sm text-gray-600 dark:text-gray-300">Close Week</span>
            <input
              type="range"
              min={1}
              max={13}
              value={closeWeek}
              onChange={event => setCloseWeek(Number(event.target.value))}
              className="flex-1 min-w-[220px]"
            />
            <span className="text-sm font-mono font-semibold text-teal-600">W{closeWeek}</span>
          </div>

          {selected && impact && (
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="lg:w-[60%] space-y-4 min-w-0">
                <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-100 dark:border-dark-border shadow-sm p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {selected.company_name}
                      </h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {selected.location} • {selected.service_lines.join(', ')}
                      </p>
                    </div>
                    <RiskBadge level={selected.risk_level} />
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <KpiCard label="Annual Revenue" value={fmt.compact(selected.annual_revenue)} />
                    <KpiCard label="EBITDA" value={fmt.compact(selected.ebitda)} />
                    <KpiCard label="Asking Price" value={fmt.compact(selected.asking_price)} />
                    <KpiCard label="Cash Score" value={scoreTotal(assessment)?.toString() ?? '—'} />
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-lg border border-gray-100 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2 p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Deal Structure</div>
                      <div className="font-medium text-gray-900 dark:text-gray-100 mt-1">
                        {selected.deal_structure}
                      </div>
                    </div>
                    <div className="rounded-lg border border-gray-100 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2 p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Integration Weeks</div>
                      <div className="font-mono font-semibold text-gray-900 dark:text-gray-100 mt-1">
                        {selected.integration_weeks}
                      </div>
                    </div>
                    <div className="rounded-lg border border-gray-100 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2 p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Owner Intent</div>
                      <div className="font-medium text-gray-900 dark:text-gray-100 mt-1">
                        {selected.owner_intent}
                      </div>
                    </div>
                    <div className="rounded-lg border border-gray-100 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2 p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Strategic Fit</div>
                      <div className="font-medium text-gray-900 dark:text-gray-100 mt-1">
                        {selected.strategic_fit}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:w-[40%] space-y-4 min-w-0">
                <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-100 dark:border-dark-border shadow-sm p-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    13-Week Cash Impact
                  </h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <ComposedChart data={impact.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke={colors.chart.gridLine} />
                      <XAxis dataKey="week" tick={{ fill: colors.text.muted, fontSize: 10 }} />
                      <YAxis
                        tickFormatter={(value: number) => fmt.compact(value)}
                        tick={{ fill: colors.text.muted, fontSize: 10 }}
                        width={56}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: colors.bg.card,
                          border: '1px solid #e5e7eb',
                          borderRadius: 8,
                        }}
                        labelStyle={{ color: colors.text.primary, fontSize: 11 }}
                        formatter={(value: unknown, name: unknown) => [
                          fmt.compact(value as number),
                          (name as string) === 'baseline' ? 'Baseline' : 'Adjusted',
                        ]}
                      />
                      <Area
                        type="monotone"
                        dataKey="baseline"
                        fill={colors.chart.areaFill}
                        stroke="none"
                      />
                      <Line
                        type="monotone"
                        dataKey="baseline"
                        stroke={colors.chart.baseline}
                        strokeWidth={2}
                        dot={false}
                        name="baseline"
                      />
                      <Line
                        type="monotone"
                        dataKey="adjusted"
                        stroke={colors.chart.adjusted}
                        strokeWidth={2}
                        strokeDasharray="6 3"
                        dot={false}
                        name="adjusted"
                      />
                      {facility && (
                        <ReferenceLine
                          y={facility.minimum_cash_covenant}
                          stroke={colors.status.red}
                          strokeDasharray="4 4"
                          label={{
                            value: 'Covenant min',
                            fill: colors.status.red,
                            fontSize: 9,
                          }}
                        />
                      )}
                      <ReferenceLine
                        x={`W${closeWeek}`}
                        stroke={colors.text.muted}
                        strokeDasharray="4 4"
                        label={{ value: 'Close', fill: colors.text.muted, fontSize: 9 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <KpiCard
                    label="Cash at Close"
                    value={fmt.compact(impact.cashAtClose)}
                    color={colors.status.red}
                  />
                  <KpiCard
                    label="Min Headroom Post"
                    value={fmt.compact(impact.minHeadroomPost)}
                    color={
                      impact.minHeadroomPost > 5_000_000
                        ? colors.status.green
                        : impact.minHeadroomPost > 0
                          ? colors.status.yellow
                          : colors.status.red
                    }
                    subtext={impact.minHeadroomPost < 0 ? 'Below covenant floor' : undefined}
                  />
                  <KpiCard
                    label="Weeks to Cash Positive"
                    value={
                      impact.weeksToCashPositive == null
                        ? '—'
                        : impact.weeksToCashPositive <= 0
                          ? 'Immediate'
                          : `${impact.weeksToCashPositive} wks`
                    }
                    color={
                      impact.weeksToCashPositive == null
                        ? colors.text.muted
                        : impact.weeksToCashPositive <= 4
                          ? colors.status.green
                          : colors.status.yellow
                    }
                  />
                  <KpiCard
                    label="Monthly Contribution at Ramp"
                    value={fmt.compact(impact.monthlyContributionAtRamp)}
                    color={colors.accent.teal}
                  />
                </div>

                <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-100 dark:border-dark-border shadow-sm p-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Revolver Utilization
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: 'Before', drawn: beforeDrawn, total: totalCommit },
                      { label: 'After', drawn: afterDrawn, total: totalCommit },
                    ].map(({ label, drawn, total }) => {
                      const pct = (drawn / total) * 100
                      return (
                        <div key={label}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-600 dark:text-gray-300">{label}</span>
                            <span className="font-mono text-gray-900 dark:text-gray-100">
                              {fmt.pct(pct)}
                            </span>
                          </div>
                          <div className="h-3 bg-gray-100 dark:bg-dark-surface-2 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${Math.min(pct, 100)}%`,
                                backgroundColor:
                                  pct > 85
                                    ? colors.status.red
                                    : pct > 70
                                      ? colors.status.yellow
                                      : colors.accent.teal,
                              }}
                            />
                          </div>
                          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 font-mono">
                            {fmt.compact(drawn)} / {fmt.compact(total)}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-100 dark:border-dark-border shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-dark-border">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Deal Comparison
              </h3>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                All scenarios use close week W4.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 dark:bg-dark-surface-2 text-gray-500 dark:text-gray-400">
                    {[
                      'Company',
                      'Revenue',
                      'EBITDA',
                      'Ask Price',
                      'Cash at Close',
                      'Min Headroom',
                      'Weeks to +Cash',
                      'Risk',
                    ].map(header => (
                      <th key={header} className="px-3 py-2 text-left font-medium whitespace-nowrap">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <tr
                      key={row.target.company_name}
                      onClick={() => setSelectedIdx(index)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          setSelectedIdx(index)
                        }
                      }}
                      tabIndex={0}
                      className={`border-t border-gray-100 dark:border-dark-border cursor-pointer transition-colors outline-none focus-visible:ring-2 focus-visible:ring-teal-500/40 ${
                        selectedIdx === index
                          ? 'bg-teal-50/50 dark:bg-teal-950/10'
                          : 'hover:bg-gray-50 dark:hover:bg-dark-surface-2/40'
                      }`}
                    >
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100 font-medium whitespace-nowrap">
                        {row.target.company_name}
                      </td>
                      <td className="px-3 py-2 font-mono text-gray-900 dark:text-gray-100">
                        {fmt.compact(row.target.annual_revenue)}
                      </td>
                      <td className="px-3 py-2 font-mono text-gray-900 dark:text-gray-100">
                        {fmt.compact(row.target.ebitda)}
                      </td>
                      <td className="px-3 py-2 font-mono text-gray-900 dark:text-gray-100">
                        {fmt.compact(row.target.asking_price)}
                      </td>
                      <td className="px-3 py-2 font-mono text-red-600">
                        {fmt.compact(row.cashAtClose)}
                      </td>
                      <td
                        className="px-3 py-2 font-mono"
                        style={{
                          color:
                            row.minHeadroomPost > 5_000_000
                              ? colors.status.green
                              : row.minHeadroomPost > 0
                                ? colors.status.yellow
                                : colors.status.red,
                        }}
                      >
                        {fmt.compact(row.minHeadroomPost)}
                      </td>
                      <td className="px-3 py-2 font-mono text-gray-900 dark:text-gray-100">
                        {row.weeksToCashPositive == null ? '—' : `${row.weeksToCashPositive}`}
                      </td>
                      <td className="px-3 py-2">
                        <RiskBadge level={row.target.risk_level} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
